import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, List, ListItem, ListItemText, ListItemIcon,
  IconButton, Button, Chip, Divider, Switch, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Tab, Tabs, CircularProgress, Alert,
} from '@mui/material';
import {
  NotificationsActive as BellIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Delete as DeleteIcon,
  DoneAll as DoneAllIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { api } from '../services/apiClient';
import { API_ENDPOINTS } from '../config/api';

interface Notification {
  id: number;
  type: string;
  title: string;
  message: string;
  link_url?: string;
  entity_type?: string;
  entity_id?: number;
  is_read: number;
  read_at?: string;
  created_at: string;
}

interface NotificationPreferences {
  in_app_enabled: number;
  email_enabled: number;
  sms_enabled: number;
  appointment_reminders: number;
  prescription_ready: number;
  lab_results_ready: number;
  invoice_due: number;
  low_stock_alerts: number;
  system_announcements: number;
}

const typeIcon = (type: string) => {
  switch (type) {
    case 'warning': return <WarningIcon color="warning" />;
    case 'success': return <SuccessIcon color="success" />;
    default: return <InfoIcon color="info" />;
  }
};

const formatRelative = (dateStr: string) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const NotificationsPage = () => {
  const [tab, setTab] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [prefsOpen, setPrefsOpen] = useState(false);
  const [prefs, setPrefs] = useState<NotificationPreferences | null>(null);
  const [prefsLoading, setPrefsLoading] = useState(false);
  const [prefsSaving, setPrefsSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = tab === 1 ? '?unread_only=1' : '';
      const res = await api.get<{ data: Notification[]; unread: number }>(`${API_ENDPOINTS.NOTIFICATIONS.LIST}${params}`);
      setNotifications(res.data ?? []);
      setUnread(res.unread ?? 0);
    } catch {
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const markRead = async (id: number) => {
    await api.patch(API_ENDPOINTS.NOTIFICATIONS.MARK_READ(id), {});
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: 1 } : n));
    setUnread(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await api.patch(API_ENDPOINTS.NOTIFICATIONS.MARK_ALL_READ, {});
    setNotifications(prev => prev.map(n => ({ ...n, is_read: 1 })));
    setUnread(0);
  };

  const deleteNotif = async (id: number) => {
    await api.delete(API_ENDPOINTS.NOTIFICATIONS.DELETE(id));
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const openPrefs = async () => {
    setPrefsOpen(true);
    setPrefsLoading(true);
    try {
      const data = await api.get<NotificationPreferences>(API_ENDPOINTS.NOTIFICATIONS.PREFERENCES);
      setPrefs(data);
    } finally {
      setPrefsLoading(false);
    }
  };

  const savePrefs = async () => {
    if (!prefs) return;
    setPrefsSaving(true);
    try {
      await api.put(API_ENDPOINTS.NOTIFICATIONS.PREFERENCES, prefs);
      setPrefsOpen(false);
    } finally {
      setPrefsSaving(false);
    }
  };

  const togglePref = (key: keyof NotificationPreferences) => {
    if (!prefs) return;
    setPrefs({ ...prefs, [key]: prefs[key] === 1 ? 0 : 1 });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BellIcon />
          <Typography variant="h5">Notifications</Typography>
          {unread > 0 && <Chip label={unread} color="error" size="small" />}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {unread > 0 && (
            <Button startIcon={<DoneAllIcon />} size="small" onClick={markAllRead}>
              Mark all read
            </Button>
          )}
          <Button startIcon={<SettingsIcon />} size="small" variant="outlined" onClick={openPrefs}>
            Preferences
          </Button>
        </Box>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="All" />
        <Tab label={`Unread${unread > 0 ? ` (${unread})` : ''}`} />
      </Tabs>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <BellIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography color="text.secondary">No notifications</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {notifications.map((n, i) => (
              <Box key={n.id}>
                {i > 0 && <Divider />}
                <ListItem
                  sx={{ bgcolor: n.is_read ? 'transparent' : 'action.hover', cursor: 'pointer' }}
                  onClick={() => { if (!n.is_read) markRead(n.id); }}
                  secondaryAction={
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteNotif(n.id); }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>{typeIcon(n.type)}</ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={n.is_read ? 400 : 700}>
                          {n.title}
                        </Typography>
                        {!n.is_read && <Chip label="New" size="small" color="primary" sx={{ height: 16, fontSize: 10 }} />}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">{n.message}</Typography>
                        <Typography variant="caption" color="text.disabled">{formatRelative(n.created_at)}</Typography>
                      </Box>
                    }
                  />
                </ListItem>
              </Box>
            ))}
          </List>
        )}
      </Paper>

      {/* Preferences Dialog */}
      <Dialog open={prefsOpen} onClose={() => setPrefsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Notification Preferences</DialogTitle>
        <DialogContent>
          {prefsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>
          ) : prefs && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Channels</Typography>
              <FormControlLabel control={<Switch checked={prefs.in_app_enabled === 1} onChange={() => togglePref('in_app_enabled')} />} label="In-app notifications" />
              <FormControlLabel control={<Switch checked={prefs.email_enabled === 1} onChange={() => togglePref('email_enabled')} />} label="Email notifications" />
              <FormControlLabel control={<Switch checked={prefs.sms_enabled === 1} onChange={() => togglePref('sms_enabled')} />} label="SMS notifications" />
              <Divider />
              <Typography variant="subtitle2" color="text.secondary">Alert Types</Typography>
              <FormControlLabel control={<Switch checked={prefs.appointment_reminders === 1} onChange={() => togglePref('appointment_reminders')} />} label="Appointment reminders" />
              <FormControlLabel control={<Switch checked={prefs.prescription_ready === 1} onChange={() => togglePref('prescription_ready')} />} label="Prescription ready" />
              <FormControlLabel control={<Switch checked={prefs.lab_results_ready === 1} onChange={() => togglePref('lab_results_ready')} />} label="Lab results ready" />
              <FormControlLabel control={<Switch checked={prefs.invoice_due === 1} onChange={() => togglePref('invoice_due')} />} label="Invoice due alerts" />
              <FormControlLabel control={<Switch checked={prefs.low_stock_alerts === 1} onChange={() => togglePref('low_stock_alerts')} />} label="Low stock alerts" />
              <FormControlLabel control={<Switch checked={prefs.system_announcements === 1} onChange={() => togglePref('system_announcements')} />} label="System announcements" />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPrefsOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={savePrefs} disabled={prefsSaving}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NotificationsPage;
